import React, { useCallback } from 'react'
import styled from 'styled-components'
import { Box, Flex, Text, H2 } from '@revolut/ui-kit'
import { Loader } from 'components/Loader'
import { BackButton } from '../BackButton'

const ENTER_KEYCODE = 13

const Wrapper = styled(Flex)`
  position: relative;
`

const ContentWrapper = styled(Flex).attrs({
  flex: '3',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  maxWidth: '34rem',
  width: '100%',
  mx: 'auto',
  px: { all: '1.5rem', md: '0' },
})`
  min-height: 100%;
`

type HeaderType = { title: Props['title']; subtitle: Props['subtitle'] }

const Header: React.FC<HeaderType> = ({ title, subtitle }) => (
  <Flex flexDirection='column' justifyContent='flex-start'>
    <H2 mt={{ all: 0, md: '1rem' }} mb='1rem' maxWidth='100%'>
      {title}
    </H2>
    {subtitle && (
      <Text variant='secondary' color='grey-50'>
        {subtitle}
      </Text>
    )}
  </Flex>
)

export type Props = {
  title?: string
  subtitle?: string
  buttonText?: string
  onSubmit?: () => void
  onBack?: () => void
  isSubmitting?: boolean
  isLoading?: boolean
} & React.ComponentProps<typeof Box>

export const Slide: React.FC<Props> = ({
  onSubmit,
  buttonText = 'Continue',
  isSubmitting = false,
  isLoading = false,
  onBack,
  children,
  title,
  subtitle,
  ...rest
}) => {
  const handleOnEnter = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.keyCode === ENTER_KEYCODE) {
        onSubmit && onSubmit()
      }
    },
    [onSubmit],
  )

  return (
    <Wrapper
      pb='2rem'
      pt={{
        all: '2rem',
        md: '3rem',
      }}
      px={{
        all: undefined,
        md: '2rem',
      }}
      justifyContent='space-between'
      flexDirection='column'
      minHeight='100vh'
      width={{
        all: '100%',
        md: '66%',
      }}
      {...rest}
    >
      <BackButton onBack={onBack} />
      <ContentWrapper>
        {isLoading ? (
          <Flex alignItems='center' minHeight='100vh'>
            <Loader />
          </Flex>
        ) : (
          <>
            <Header subtitle={subtitle} title={title} />
            <Flex
              onKeyDown={handleOnEnter}
              flex='1'
              flexDirection='column'
              justifyContent='center'
              minWidth={{ all: '100%', md: '28rem' }}
            >
              {children}
            </Flex>
          </>
        )}
      </ContentWrapper>
    </Wrapper>
  )
}
